import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth/hooks';
import { supabase } from '../../../lib/supabase';
import { GalleryUpload } from './components/GalleryUpload';
import toast from 'react-hot-toast';

interface Settings {
  gallery: {
    items: Array<{
      id: string;
      type: 'image' | 'video';
      url: string;
    }>;
  };
  about: {
    content: string;
  };
  social: {
    facebook?: string;
    instagram?: string;
    whatsapp?: string;
    tiktok?: string;
    youtube?: string;
  };
}

const defaultSettings: Settings = {
  gallery: {
    items: []
  },
  about: {
    content: 'ספרו לנו קצת על העסק שלכם...'
  },
  social: {}
};

export default function ExternalPageSettings() {
  const { user, business } = useAuth();
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [businessData, setBusinessData] = useState<any>(null);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    const fetchBusinessData = async () => {
      try {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('business_id')
          .eq('id', user.id)
          .single();

        if (userError) throw userError;
        if (!userData?.business_id) throw new Error('לא נמצא עסק מקושר');

        setBusinessId(userData.business_id);

        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select(`
            *,
            business_hours (
              regular_hours,
              special_dates
            )
          `)
          .eq('id', userData.business_id)
          .single();

        if (businessError) throw businessError;
        setBusinessData(businessData);

        if (businessData?.external_page_settings) {
          setSettings({
            gallery: businessData.external_page_settings.gallery || defaultSettings.gallery,
            about: businessData.external_page_settings.about || defaultSettings.about,
            social: businessData.external_page_settings.social || defaultSettings.social
          });
        }
      } catch (error) {

        toast.error('שגיאה בטעינת נתוני העסק');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [user?.id]);

  const handleSave = async () => {
    if (!businessId || !hasChanges) return;

    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          external_page_settings: settings
        })
        .eq('id', businessId);

      if (error) throw error;

      toast.success('ההגדרות נשמרו בהצלחה');
      setHasChanges(false);
    } catch (error) {

      toast.error('שגיאה בשמירת ההגדרות');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/settings" className="text-gray-600 active:text-gray-900">
              <ArrowRight className="h-6 w-6" />
            </Link>
            <h1 className="text-xl font-bold text-gray-900">עיצוב דף חיצוני</h1>
          </div>
          {hasChanges && (
            <motion.button
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl active:scale-95 transition-transform"
            >
              שמור
            </motion.button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
                {/* Gallery Section */}
                {businessId && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">גלריית תמונות</h3>
                    <p className="text-sm text-gray-500 mb-4">הוסף תמונות ווידאו להצגה בדף</p>
                    <GalleryUpload
                      businessId={businessId}
                      items={settings.gallery.items}
                      onChange={(items) => {
                        setSettings(prev => ({
                          ...prev,
                          gallery: { items }
                        }));
                        setHasChanges(true);
                      }}
                    />
                  </div>
                )}

                {/* About Section */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">תיאור העסק</h3>
                  <p className="text-sm text-gray-500 mb-4">ספר ללקוחות על העסק שלך</p>
                  <textarea
                    value={settings.about.content}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        about: {
                          ...prev.about,
                          content: e.target.value
                        }
                      }));
                      setHasChanges(true);
                    }}
                    rows={10}
                    className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none text-[15px]"
                    placeholder="ספרו לנו קצת על העסק שלכם..."
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-400">{settings.about.content.length} תווים</p>
                  </div>
                </div>

                {/* Social Section */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">רשתות חברתיות</h3>
                    <p className="text-sm text-gray-500 mb-4">הוסף קישורים לפרופילים שלך</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      פייסבוק
                    </label>
                    <input
                      type="url"
                      value={settings.social.facebook || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          social: {
                            ...prev.social,
                            facebook: e.target.value
                          }
                        }));
                        setHasChanges(true);
                      }}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-[15px]"
                      placeholder="https://facebook.com/..."
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      אינסטגרם
                    </label>
                    <input
                      type="url"
                      value={settings.social.instagram || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          social: {
                            ...prev.social,
                            instagram: e.target.value
                          }
                        }));
                        setHasChanges(true);
                      }}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-[15px]"
                      placeholder="https://instagram.com/..."
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      וואטסאפ
                    </label>
                    <input
                      type="tel"
                      value={settings.social.whatsapp || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          social: {
                            ...prev.social,
                            whatsapp: e.target.value
                          }
                        }));
                        setHasChanges(true);
                      }}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-[15px]"
                      placeholder="+972-50-123-4567"
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      טיקטוק
                    </label>
                    <input
                      type="url"
                      value={settings.social.tiktok || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          social: {
                            ...prev.social,
                            tiktok: e.target.value
                          }
                        }));
                        setHasChanges(true);
                      }}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-[15px]"
                      placeholder="https://tiktok.com/@..."
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      יוטיוב
                    </label>
                    <input
                      type="url"
                      value={settings.social.youtube || ''}
                      onChange={(e) => {
                        setSettings(prev => ({
                          ...prev,
                          social: {
                            ...prev.social,
                            youtube: e.target.value
                          }
                        }));
                        setHasChanges(true);
                      }}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-[15px]"
                      placeholder="https://youtube.com/@..."
                      dir="ltr"
                    />
                  </div>
                </div>
      </div>
    </div>
  );
}
